import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NotificationMenu from 'components/notifications/menu/NotificationMenu';
import ErrorBoundary from 'components/shared/error-boundary/ErrorBoundary';
import LanguageSelector from 'components/shared/LanguageSelector';
import { fullDrawerWidth, miniDrawerWidth, sidebarGutter } from 'common/constants/general';
import routes from 'common/routes';
import { isAdmin, isSupervisor, isTrainer } from 'common/utils/user';
import StyledAppbar from 'common/theme/appbar/StyledAppbar';
import StyledAppbarCountry from 'common/theme/appbar/StyledAppbarCountry';
import StyledAppbarLogoContainer from 'common/theme/appbar/StyledAppbarLogoContainer';
import StyledAppbarMainContainer from 'common/theme/appbar/StyledAppbarMainContainer';
import StyledAppbarTitle from 'common/theme/appbar/StyledAppbarTitle';
import StyledAppbarTitleContainer from 'common/theme/appbar/StyledAppbarTitleContainer';
import StyledAppbarToolbar from 'common/theme/appbar/StyledAppbarToolbar';
import StyledAppbarUserContainer from 'common/theme/appbar/StyledAppbarUserContainer';
import StyledAppbarUsername from 'common/theme/appbar/StyledAppbarUsername';
import AppBarContext from 'context/AppBarContext';
import EchoContextProvider from 'context/EchoContextProvider';
import settingsSlice from 'store/modules/settings';

import { ReactComponent as Logo } from 'images/logotype.svg';

function AppBar() {
  const dispatch = useDispatch();
  const appBarRef = useRef(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { appBarButton, appBarTitle } = useContext(AppBarContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const language = useSelector((state) => state.settings.config.language);
  const miniDrawer = useSelector((state) => state.settings.config.miniDrawer);
  const user = useSelector((state) => state.auth.user);

  // Ancho reservado por el sidebar flotante (panel + gutter a ambos lados).
  const drawerWidth = (miniDrawer ? miniDrawerWidth : fullDrawerWidth) + sidebarGutter * 2;

  const navigate = useNavigate();

  // Inicio según rol (mismo destino que el ítem "Inicio" del menú lateral).
  let homePath = routes.clientDashboard.path;
  if (isAdmin(user.role) || isSupervisor(user.role)) homePath = routes.adminDashboard.path;
  else if (isTrainer(user.role)) homePath = routes.trainerDashboard.path;

  const handleScroll = useCallback(() => {
    const verticalScroll = window.scrollY;

    if (verticalScroll > 0 && !hasScrolled) {
      setHasScrolled(true);
    } else if (verticalScroll === 0 && hasScrolled) {
      setHasScrolled(false);
    }
  }, [hasScrolled, setHasScrolled]);

  const onLanguageChange = useCallback(
    (newLanguage) => {
      if (language !== newLanguage) {
        dispatch(
          settingsSlice.actions.update({ id: user.settings.id, input: { language: newLanguage } })
        );
      }
    },
    [dispatch, language, user.settings.id]
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled]);

  return (
    <StyledAppbar
      drawerWidth={drawerWidth}
      hasScrolled={hasScrolled}
      position="fixed"
      ref={appBarRef}>
      <StyledAppbarToolbar>
        {isDesktop && (
          <StyledAppbarTitleContainer>
            {appBarTitle && (
              <StyledAppbarTitle noWrap component="h1">
                {appBarTitle}
              </StyledAppbarTitle>
            )}
            {appBarButton}
          </StyledAppbarTitleContainer>
        )}
        <StyledAppbarMainContainer>
          <StyledAppbarLogoContainer>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(homePath)}
              aria-label="Inicio"
              sx={{ p: 0, m: 0, border: 0, background: 'none', cursor: 'pointer', display: 'block', width: '100%', '& svg': { width: '100%', height: 'auto', display: 'block' } }}>
              <Logo />
            </Box>
          </StyledAppbarLogoContainer>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: { xs: 0.5, md: 2 } }}>
            <EchoContextProvider>
              {/* Si una notificación malformada lanza al renderizar, contenerlo aquí
                  para no desmontar toda la app (ventana en blanco al abrir la campana). */}
              <ErrorBoundary name="notification-menu" fallback={() => null}>
                <NotificationMenu anchorElement={appBarRef.current} />
              </ErrorBoundary>
            </EchoContextProvider>
            <StyledAppbarUserContainer>
              <StyledAppbarUsername>{user.full_name}</StyledAppbarUsername>
              <StyledAppbarCountry>{user.country?.label}</StyledAppbarCountry>
            </StyledAppbarUserContainer>
            {/* El toggle de tema vive SOLO en el sidebar (SidebarThemeToggle), en cualquier
                breakpoint: aquí competía con la campana y el avatar y se deformaba en mobile. */}
            <LanguageSelector
              selectedLanguage={language}
              onSelect={onLanguageChange}
              color={theme.palette.text.primary}
            />
          </Box>
        </StyledAppbarMainContainer>
      </StyledAppbarToolbar>
    </StyledAppbar>
  );
}

export default AppBar;
